import {Chip} from "@progress/kendo-react-buttons"
import {ComboBoxChangeEvent, ComboBoxFilterChangeEvent, ListItemProps} from "@progress/kendo-react-dropdowns"
import {Field} from "@progress/kendo-react-form"
import {Loader} from "@progress/kendo-react-indicators"
import {useInternationalization} from "@progress/kendo-react-intl"
import dayjs from "dayjs"
import * as React from "react"
import {BookingDetail, DealerTestPackage, setTestPackagePopupShow, TestPackageDetail} from "../../../containers/MotBooking/bookingSlice"
import {RootState} from "../../../rootReducer"
import {useAppDispatch, useAppSelector} from "../../../store"
import BookingMultiColumnComboBox from "../../Shared/multiColumnComboBox"
import TestChipWithDetail from "./testChipWithDetail"
import TestPackagePopUp from "./testPackagePopUp"

const TestPackagesMultiCombo = ({
    testPackageId,
    booking,
    updateBookingDetail,
}: {
    testPackageId: number | null
    booking: BookingDetail
    updateBookingDetail: (booking: BookingDetail) => void
}) => {
    const intl = useInternationalization()
    const dispatch = useAppDispatch()
    const gettingTestPackages = useAppSelector((state: RootState) => state.containers.motBooking.gettingTestPackages)
    const dealerTestPackages = useAppSelector((state: RootState) => state.containers.motBooking.dealerTestPackages)

    const gettingPackageDetail = useAppSelector((state: RootState) => state.containers.motBooking.gettingPackageDetail)
    const dvsaTests = useAppSelector((state: RootState) => state.containers.motBooking.dvsTests)

    const show = useAppSelector((state: RootState) => state.containers.motBooking.testPackagePopupShow)
    const [testPackages, setTestPackages] = React.useState<DealerTestPackage[]>(dealerTestPackages)

    const anchor = React.useRef<any>()

    React.useEffect(() => {
        setTestPackages(dealerTestPackages)
    }, [dealerTestPackages])

    const columns = [
        {
            field: "testPackageName",
            header: "Description",
            width: "550px",
        },
        {
            field: "duration",
            header: "Mins",
            width: "75px",
        },
        {
            field: "testFee",
            header: "Test Fee",
            width: "100px",
        },
        {
            field: "laneFee",
            header: "Lane fee",
            width: "100px",
        },
    ]

    const columnItemRender = (li: React.ReactElement<HTMLLIElement>, itemProps: ListItemProps) => {
        const children = columns.map((col, i) => {
            if (col.field === "testPackageName")
                return (
                    <span
                        title={itemProps.dataItem[col.field]}
                        className={`k-table-td ${
                            !itemProps.dataItem["active"] ? "k-disabled c-not-allowed" : "c-pointer"
                        }`}
                        style={{width: col.width}}
                        key={"test_pack_desc_" + crypto.randomUUID()}
                    >
                        {itemProps.dataItem[col.field]}
                    </span>
                )

            return (
                <span
                    className={`k-table-td ${
                        !itemProps.dataItem["active"] ? "k-disabled c-not-allowed" : "c-pointer"
                    }`}
                    style={{width: col.width}}
                    key={i}
                >
                    {itemProps.dataItem[col.field]}
                </span>
            )
        
        })
        return React.cloneElement(li, {...li.props}, children)
    }

    const CurrencyFormatter = (value: number) => intl.formatNumber(value, "c")

    let testPackageFormatted = testPackages
        .filter(x => x.active || x.testPackageID === booking.testPackageID)
        .map(x => ({
            ...x,
            testFee: CurrencyFormatter(x.testFee),
            laneFee: CurrencyFormatter(x.laneFee),
            testPackageDescription: x.testPackageDescription ?? "",
            testPackageName: `${x.testPackageName} - ${x.testPackageDescription ?? ""}`,
        }))

    const onTestPackageSearch = (event: ComboBoxFilterChangeEvent) => {
        if (event) {
            filterMultipleColumns(event.filter.value)
        }
    }

    const filterMultipleColumns = (searchText: string) => {
        if (searchText !== null && searchText.indexOf(" ") >= 0) {
            const words = searchText.split(" ")
            const data = dealerTestPackages.filter(x =>
                words.every(w =>
                    `${x.testPackageName} - ${x.testPackageDescription ?? ""}`
                        .toLocaleLowerCase()
                        .includes(w.toLocaleLowerCase()),
                ),
            )
            setTestPackages(data)
        } else if (searchText.length >= 3) {
            const data = dealerTestPackages.filter(
                x =>
                    x.testPackageDescription?.toLocaleLowerCase().includes(searchText.toLocaleLowerCase()) ||
                    x.testPackageName?.toLocaleLowerCase().includes(searchText.toLocaleLowerCase()),
            )
            setTestPackages(data)
        } else {
            setTestPackages(dealerTestPackages)
        }
    }

    const onTestPackageChange = (ev: ComboBoxChangeEvent) => {
        let bkdt = {...booking}
        if (ev.value !== null) {
            const testPackage = ev.value as DealerTestPackage
            const data =
                dealerTestPackages.find(x => x.testPackageID === testPackage.testPackageID) || ({} as DealerTestPackage)

            //Set Custom price if available
            // const laneFees = hasCustomPrice && motCustomPrice ? motCustomPrice.value : data.laneFee ?? 0
            const laneFees = data.laneFee ?? 0
            const testFees = data.testFee ?? 0
            const noTestDate = dayjs(booking.testStart).get("hour") === 0
            bkdt = {
                ...booking,
                testPackage: {...ev.value, laneFee:laneFees, testFee:testFees} as TestPackageDetail,
                testPackageID: testPackage.testPackageID,
                laneFee: laneFees,
                testFee: testFees,
                total: laneFees + testFees,
                duration: data.duration,
                testStart: booking.testStart,
                testEnd: noTestDate
                    ? booking.testStart
                    : dayjs(booking.testStart).add(data.duration, "minutes").toString(),
            }
        } else {
            bkdt = {
                ...booking,
                testPackageID: 0,
                testEnd: booking.testStart,
                duration: 0,
                laneFee: 0,
                testFee: 0,
                total: 0,
            }
        }

        updateBookingDetail(bkdt)
    }
    const chipContent = (
        <span className="clip-text" onClick={() => dispatch(setTestPackagePopupShow(true))} ref={anchor}>
            ...
        </span>
    )

    return (
        <>
            <Field
                allowCustom={false}
                required={true}
                disabled={gettingPackageDetail}
                name={"testPackageID"}
                component={BookingMultiColumnComboBox}
                value={testPackageId}
                textField="testPackageName"
                dataItemKey="testPackageID"
                data={testPackageFormatted}
                columns={columns}
                label={"Test package"}
                itemRender={columnItemRender}
                filterable={true}
                loading={gettingTestPackages}
                onFilterChange={onTestPackageSearch}
                onChange={onTestPackageChange}
                validationMessage={"Select a test package"}
            />
            <div style={{height: "58px", marginBottom: "8px"}}>
                {gettingPackageDetail && <Loader size="small" type={"pulsing"} />}
                {dvsaTests !== null && dvsaTests !== undefined && dvsaTests.length > 0 && (
                    <>
                        <span
                            className="d-flex "
                            style={{fontSize: "calc(14px - 25%)", lineHeight: "16px", color: "rgba(0, 0, 0, 0.54)"}}
                        >
                            Tests
                        </span>
                        {dvsaTests.map(
                            (item, ind) => ind <= 2 && <TestChipWithDetail key={crypto.randomUUID()} test={item} />,
                        )}
                        {dvsaTests.length > 3 && <Chip children={chipContent} className="me-2" />}
                    </>
                )}
            </div>
            <TestPackagePopUp show={show} anchor={anchor} />
        </>
    )
}

export default React.memo(TestPackagesMultiCombo)
